import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
// UNCANNY CUSTOM START (INP fix 12.10.26) - do not remove
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * On phones the search modal already fills the whole screen with no backdrop, so it can open as a
 * plain (non-modal) dialog. showModal() makes the rest of the page inert, which forces the browser
 * to restyle every product card: ~150 ms of the ~220 ms tap delay on a mid-range phone. Focus is
 * trapped by hand instead, and aria-modal keeps screen readers inside the dialog.
 * @param {HTMLElement} component
 * @returns {boolean}
 */
function opensWithoutInert(component) {
  return component.id === 'search-modal' && window.matchMedia('(max-width: 749px)').matches;
}

/** Inline styles that give the non-modal dialog the same full-screen box and stacking as the modal one. */
const NON_MODAL_STYLES = { position: 'fixed', inset: '0', margin: '0', overflow: 'auto', 'z-index': '2147483001' };
// UNCANNY CUSTOM END

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
    if (this.#openedWithoutInert) removeTrapFocus(); // UNCANNY CUSTOM (INP fix 12.10.26)
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  // UNCANNY CUSTOM (INP fix 12.10.26): true while the dialog is open via show() instead of showModal()
  #openedWithoutInert = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      // UNCANNY CUSTOM START (INP fix 12.10.26) - do not remove
      if (opensWithoutInert(this)) {
        for (const [property, value] of Object.entries(NON_MODAL_STYLES)) dialog.style.setProperty(property, value);
        dialog.setAttribute('aria-modal', 'true');
        // Trap first: trapFocus() focuses its container, which would pull focus off the search box
        trapFocus(dialog);
        dialog.show();
        if (!dialog.contains(document.activeElement)) {
          dialog.querySelector('[autofocus], input:not([type=hidden]), button:enabled')?.focus();
        }
        this.#openedWithoutInert = true;
      } else {
        dialog.showModal();
      }
      // UNCANNY CUSTOM END
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // UNCANNY CUSTOM START (INP fix 12.10.26) - do not remove
    if (this.#openedWithoutInert) {
      removeTrapFocus();
      dialog.removeAttribute('aria-modal');
      for (const property of Object.keys(NON_MODAL_STYLES)) dialog.style.removeProperty(property);
      this.#openedWithoutInert = false;
    }
    // UNCANNY CUSTOM END

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
